import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Form } from "@/shared/ui/form";
import { Label } from "@/shared/ui/label";
import {
  ReceivableCurrency,
  ReceivableTaxType,
  ReceivableTaxDocument,
  PendingReceivableTax,
  PendingReceivableTaxDocument,
} from "../../utils/types";

import {
  taxFormSchema,
  TaxFormValues,
} from "./utils";
import { TaxFormFields } from "./TaxFormFields";
import { TaxDocumentForm } from "./TaxDocumentForm";
import { TaxDocumentList } from "./TaxDocumentList";
import { useTaxDocuments } from "./hooks/useTaxDocuments";
import { TaxListItem } from "./utils";

interface TaxEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tax: TaxListItem | null;
  existingDocuments?: ReceivableTaxDocument[];
  loadingDocuments?: boolean;
  onSubmit: (
    values: TaxFormValues,
    pendingDocuments: PendingReceivableTax["documents"]
  ) => Promise<void>;
  onDocumentAdd?: (document: { documentType: string; description?: string; file: File }) => Promise<void>;
  onDocumentRemove?: (document: ReceivableTaxDocument | PendingReceivableTaxDocument) => Promise<void>;
  disabled?: boolean;
  isDocumentMutationPending?: boolean;
  /** Quando true, permite editar o valor da taxa mesmo para taxas já salvas (ex.: recebível ainda pendente). */
  canEditTaxValue?: boolean;
}

export function TaxEditDialog({
  open,
  onOpenChange,
  tax,
  existingDocuments = [],
  loadingDocuments = false,
  onSubmit,
  onDocumentAdd,
  onDocumentRemove,
  disabled = false,
  isDocumentMutationPending = false,
  canEditTaxValue = false,
}: TaxEditDialogProps) {
  const form = useForm<TaxFormValues>({
    resolver: zodResolver(taxFormSchema),
    defaultValues: {
      currency: ReceivableCurrency.BRL,
      type: ReceivableTaxType.IOF,
      description: "",
      document: "",
      value: 0,
    },
  });

  const documentsHook = useTaxDocuments();
  const isSubmitting = form.formState.isSubmitting;
  const prevOpenRef = useRef(false);
  const initializedTaxIdRef = useRef<string | null>(null);

  // Inicializar formulário apenas quando o dialog abre (não em re-renders)
  useEffect(() => {
    if (tax && open) {
      const base = tax.tax;
      const taxId = base.id;

      // Só reseta quando o dialog acaba de abrir ou quando troca de taxa
      const shouldInitialize = !prevOpenRef.current || initializedTaxIdRef.current !== taxId;

      if (shouldInitialize) {
        initializedTaxIdRef.current = taxId;
        const resolvedType = Object.values(ReceivableTaxType).includes(base.type as ReceivableTaxType)
          ? (base.type as ReceivableTaxType)
          : ReceivableTaxType.OTHER;

        const numValue = typeof base.value === "number" ? base.value : Number(base.value) || 0;

        form.reset({
          currency: base.currency,
          type: resolvedType,
          description: base.description ?? "",
          document: base.document ?? "",
          value: numValue,
        });
        // Carrega documentos existentes ao editar taxa pendente
        const initialDocs = tax.kind === "pending" ? base.documents ?? [] : [];
        documentsHook.reset(initialDocs);
      }
      prevOpenRef.current = true;
    } else {
      prevOpenRef.current = false;
      initializedTaxIdRef.current = null;
      if (!open) {
        form.reset({
          currency: ReceivableCurrency.BRL,
          type: ReceivableTaxType.IOF,
          description: "",
          document: "",
          value: 0,
        });
        documentsHook.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form e documentsHook causam loop (documentsHook retorna novo objeto a cada render)
  }, [tax, open]);

  const handleDocumentAdd = async () => {
    if (!documentsHook.draft.file || !documentsHook.draft.documentType) return;
    
    if (onDocumentAdd && tax?.kind === "existing") {
      await onDocumentAdd({
        documentType: documentsHook.draft.documentType,
        description: documentsHook.draft.description || undefined,
        file: documentsHook.draft.file,
      });
      documentsHook.reset();
    } else {
      documentsHook.addDocument();
    }
  };

  const handleDocumentRemove = async (document: ReceivableTaxDocument | PendingReceivableTaxDocument) => {
    if (onDocumentRemove) {
      await onDocumentRemove(document);
    } else {
      documentsHook.removeDocument(document.id);
    }
  };

  const handleSubmit = async (values: TaxFormValues) => {
    if (disabled || isSubmitting || !tax) return;
    await onSubmit(values, documentsHook.documents);
  };

  const isBusy = disabled || isSubmitting || isDocumentMutationPending;
  const totalDocuments = existingDocuments.length + documentsHook.documents.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar taxa</DialogTitle>
          <DialogDescription>Ajuste as informações da taxa conforme necessário.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(handleSubmit)(e);
            }}
            className="space-y-4"
          >
            <TaxFormFields
              form={form}
              disabled={isBusy}
              valueDisabled={tax?.kind === "existing" && !canEditTaxValue}
            />

            <div className="space-y-4 rounded-lg border border-white/10 bg-muted/15 p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Documentos da taxa (opcional)</h3>
                  <p className="text-xs text-muted-foreground">
                    {tax?.kind === "existing" && !canEditTaxValue
                      ? "Adicione novos anexos. Valores e remoção estão bloqueados."
                      : "Adicione novos anexos ou remova os já vinculados."}
                  </p>
                </div>
                {totalDocuments > 0 && (
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {totalDocuments} documento{totalDocuments !== 1 ? "s" : ""}
                  </Label>
                )}
              </div>

              <TaxDocumentForm
                draft={documentsHook.draft}
                onDraftChange={documentsHook.updateDraft}
                onAdd={handleDocumentAdd}
                error={documentsHook.error}
                disabled={isBusy}
                documentsCount={totalDocuments}
                fileInputRef={documentsHook.fileInputRef}
                acceptedTypes={documentsHook.acceptedTypes}
                onFileChange={documentsHook.handleFileChange}
                onFileButtonClick={() => documentsHook.handleFileButtonClick(isBusy)}
                onFileRemove={documentsHook.handleFileRemove}
              />

              {documentsHook.documents.length > 0 && (
                <TaxDocumentList
                  documents={documentsHook.documents}
                  onRemove={handleDocumentRemove}
                  disabled={isBusy || (tax?.kind === "existing" && !canEditTaxValue)}
                  title="Documentos adicionados nesta edição"
                />
              )}

              {tax?.kind === "existing" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Documentos vinculados ({existingDocuments.length})
                    </Label>
                    {loadingDocuments && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  {existingDocuments.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum documento anexado.</p>
                  ) : (
                    <TaxDocumentList
                      documents={existingDocuments}
                      onRemove={handleDocumentRemove}
                      disabled={isBusy || !canEditTaxValue}
                    />
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenChange(false);
                }}
                disabled={isBusy}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isBusy || isSubmitting} onClick={(e) => e.stopPropagation()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar alterações"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

